import { IIcon } from '@components/std-components/TopBar'
import { View } from '@modules/order/constants/views'
import OrderContext from '@modules/order/contexts/order-context'
import { useObserver } from 'mobx-react-lite'
import React, { FC, useContext, useMemo, useEffect } from 'react'
import EnhancedProductViewContainer from './ProductsView'
import EnhancedOrderTopbar from '../../components/OrderTopbar/EnhancedOrderTopbar'
import EhancedAddProductViewContainer from './EnhancedAddProduct'
import EnhancedListViewContainer from './EnhancedListView/EnhancedListViewContainer'
import { stores } from '@stores'
import EnhancedKPIViewContainer from './EnhancedKPIView'
import HierarchyWiseItemsTableStore from '@components/UI-Components/HierarchyWiseItemsTable/stores/HierarchyWiseItemsTableStore'
import TableContext from '@components/UI-Components/HierarchyWiseItemsTable/contexts/table-context'
import { orderTemplatePreferencesStore } from '@modules/order/stores/order/OrderTemplatePreferencesStore'

export interface EnhancedViewFactoryProps {
  baseUrlNavIconConfig?: IIcon
}

const EnhancedViewFactory: FC<EnhancedViewFactoryProps> = ({ baseUrlNavIconConfig }) =>
  useObserver(function useHook() {
    const {
      uiStore: {
        view,
        setView,
        dataStore: { getTableSetting }
      }
    } = useContext(OrderContext)

    const {
      nav: {
        queryParams: { isAddProductToOrderView, subSection, orderId }
      },
      productConfiguration: { loadedConfigData }
    } = stores
    const { fetchOrderPreferences } = orderTemplatePreferencesStore

    useEffect(() => {
      if (isAddProductToOrderView) {
        setView(View.AddProduct)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAddProductToOrderView])

    useEffect(() => {
      if (orderId) {
        getTableSetting()
      }
    }, [orderId, getTableSetting])

    useEffect(() => {
      if (orderId) {
        fetchOrderPreferences()
      }
    }, [orderId, fetchOrderPreferences])

    const tableStoreInstance = useMemo(() => {
      const tableStore = new HierarchyWiseItemsTableStore()
      return tableStore
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, subSection])

    const tableContextValue: any = useMemo(
      () => ({
        tableStore: tableStoreInstance,
        translationKey: undefined
      }),
      [tableStoreInstance]
    )

    if (!loadedConfigData) {
      return null
    }

    let viewComponent = null
    switch (view) {
      case View.Product:
        viewComponent = <EnhancedProductViewContainer />
        break
      case View.KPI:
        viewComponent = tableStoreInstance ? (
          <EnhancedKPIViewContainer
            tableStoreInstance={tableStoreInstance}
            tableContextValue={tableContextValue}
          />
        ) : null
        break
      case View.List:
        viewComponent = tableStoreInstance ? (
          <EnhancedListViewContainer
            tableStoreInstance={tableStoreInstance}
            tableContextValue={tableContextValue}
          />
        ) : null
        break
      case View.AddProduct:
        viewComponent = <EhancedAddProductViewContainer />
        break
      default:
        viewComponent = <EnhancedProductViewContainer />
    }
    return (
      <>
        <TableContext.Provider value={tableContextValue}>
          <EnhancedOrderTopbar
            baseUrlNavIconConfig={baseUrlNavIconConfig}
            tableStoreInstance={tableStoreInstance}
          />
          {viewComponent}
        </TableContext.Provider>
      </>
    )
  })

export default EnhancedViewFactory
